import React, { createContext, useContext, useEffect, useState } from "react";
import Axios from "../services/Axios";
import APIVariables from "../services/api-variables";
import LocalStorage from "../shared/localStorage";
import { Auth } from "../routes/Constants";

const AuthContext = createContext(null);

/**
 * Single source of truth for "logged in": user is set only after GET /users/profile succeeds.
 * On load, if localStorage has a user we re-validate with the backend; on 401 we clear and redirect.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(LocalStorage.USER_NHSPORTAL);
    if (!raw) {
      setValidated(true);
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      setValidated(true);
      return;
    }
    if (!parsed?.isLoggedIn) {
      setValidated(true);
      return;
    }

    // Confirm session with backend (cookies); then expose user to the tree.
    Axios.get(APIVariables.USERS_PROFILE)
      .then(() => {
        setUser(parsed);
        setValidated(true);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(LocalStorage.USER_NHSPORTAL);
          const message =
            err?.response?.data?.tokenExpired || err?.response?.data?.detail;
          if (message) {
            localStorage.setItem(
              LocalStorage.EXPIRED_SESSION_MESSAGE_NHSPORTAL,
              typeof message === "string" ? message : JSON.stringify(message)
            );
          }
          window.location.href = Auth.LOGIN;
          return;
        }
        setValidated(true);
      });
  }, []);

  if (!validated) {
    // Still validating session; avoid rendering app with stale auth.
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          fontSize: "1rem",
        }}
      >
        Validating session…
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isLoggedIn: Boolean(user?.isLoggedIn),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  return ctx;
}
